import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// -----------------------------------------------------------------------------
// Tunable marketing estimates. These are intentionally conservative illustrative
// figures, NOT a precise financial model. Adjust here if messaging changes.
// -----------------------------------------------------------------------------
const WEEKS_PER_MONTH = 4.3;
const GROCERY_WASTE_REDUCTION = 0.15; // a precise list trims ~15% overbuying/waste
const TAKEOUT_AVG_COST = 12; // avg spend per person on one takeout / eat-out
const HOME_MEAL_AVG_COST = 4; // avg cost to cook the same meal at home per person
const TAKEOUTS_REPLACED_FRACTION = 0.5; // ~half of takeouts become planned meals
const PLANNING_MINUTES_PER_WEEK = 90; // deciding + list-making + extra store time

// Input ranges
const TAKEOUT_MIN = 0;
const TAKEOUT_MAX = 14;
const GROCERY_MIN = 20;
const GROCERY_MAX = 300;
const HOUSEHOLD_MIN = 1;
const HOUSEHOLD_MAX = 6;

// Branded slider thumb + track styling. appearance-none strips the native
// control, so the thumb must be drawn manually for both WebKit and Firefox.
const sliderStyles = `
  .ue-range {
    -webkit-appearance: none;
    appearance: none;
    height: 8px;
    border-radius: 9999px;
    outline: none;
  }
  .ue-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 9999px;
    background: #ffffff;
    border: 2px solid #0d0c07;
    box-shadow: 0 1px 3px rgba(35, 33, 22, 0.25);
    cursor: pointer;
    transition: transform 120ms ease;
  }
  .ue-range::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 9999px;
    background: #ffffff;
    border: 2px solid #0d0c07;
    box-shadow: 0 1px 3px rgba(35, 33, 22, 0.25);
    cursor: pointer;
  }
  .ue-range:active::-webkit-slider-thumb { transform: scale(1.1); }
  .ue-range:focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 4px rgba(13, 12, 7, 0.25);
  }
  .ue-range:focus-visible::-moz-range-thumb {
    box-shadow: 0 0 0 4px rgba(13, 12, 7, 0.25);
  }
  @media (prefers-reduced-motion: reduce) {
    .ue-range::-webkit-slider-thumb { transition: none; }
  }
`;

const Slider = ({
  id,
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  valueLabel,
}) => {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={id}
          className="text-[14px] font-medium text-[#0d0c07] leading-[22px]"
        >
          {label}
        </label>
        <span className="text-[15px] font-bold text-[#0d0c07] leading-[22px] whitespace-nowrap tabular-nums">
          {valueLabel}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuetext={valueLabel}
        style={{
          background: `linear-gradient(to right, #FFCB00 0%, #FFCB00 ${pct}%, #ebebea ${pct}%, #ebebea 100%)`,
        }}
        className="ue-range w-full cursor-pointer"
      />
    </div>
  );
};

const SavingsCalculator = ({ currencySymbol = "$" }) => {
  const { t } = useTranslation("pages");

  const [takeout, setTakeout] = useState(3);
  const [grocery, setGrocery] = useState(70);
  const [household, setHousehold] = useState(2);

  const { moneyPerMonth, hoursPerMonth } = useMemo(() => {
    const grocerySaved = grocery * GROCERY_WASTE_REDUCTION * WEEKS_PER_MONTH;
    const takeoutSaved =
      takeout *
      TAKEOUTS_REPLACED_FRACTION *
      (TAKEOUT_AVG_COST - HOME_MEAL_AVG_COST) *
      household *
      WEEKS_PER_MONTH;
    const money = Math.max(0, Math.round(grocerySaved + takeoutSaved));

    const hours = Math.round(
      (PLANNING_MINUTES_PER_WEEK * WEEKS_PER_MONTH) / 60
    );

    return { moneyPerMonth: money, hoursPerMonth: hours };
  }, [takeout, grocery, household]);

  return (
    <section className="bg-white flex relative">
      <style>{sliderStyles}</style>
      <div className="max-w-5xl flex mx-auto py-16 md:py-24 relative">
        <div className="max-w-5xl mx-auto px-10 md:px-6 pb-20">
          <div className="mb-8 max-w-2xl">
            <span className="inline-block bg-[#FFCB00] text-[#0d0c07] text-[12px] font-bold uppercase tracking-[0.08em] rounded-[6px] px-2 py-1 mb-3">
              {t("landingB.calculator.eyebrow")}
            </span>
            <h2 className="font-extrabold text-[25px] md:text-[40px] text-[#0d0c07] leading-[40px] md:leading-[48px] tracking-[-0.32px] mb-2">
              {t("landingB.calculator.title")}
            </h2>
            <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
              {t("landingB.calculator.subtitle")}
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-stretch">
            {/* Inputs */}
            <div className="md:flex-1 p-6 rounded-[12px] border border-[#e6e5e2] bg-[#F3F3F2] shadow-[0px_1px_2px_0px_rgba(35,33,22,0.06),_0px_10px_28px_0px_rgba(83,81,69,0.10)] flex flex-col gap-6">
              <Slider
                id="calc-takeout"
                label={t("landingB.calculator.takeoutLabel")}
                value={takeout}
                min={TAKEOUT_MIN}
                max={TAKEOUT_MAX}
                onChange={setTakeout}
                valueLabel={`${takeout}×`}
              />
              <Slider
                id="calc-grocery"
                label={t("landingB.calculator.groceryLabel")}
                value={grocery}
                min={GROCERY_MIN}
                max={GROCERY_MAX}
                step={5}
                onChange={setGrocery}
                valueLabel={`${currencySymbol}${grocery}`}
              />
              <Slider
                id="calc-household"
                label={t("landingB.calculator.householdLabel")}
                value={household}
                min={HOUSEHOLD_MIN}
                max={HOUSEHOLD_MAX}
                onChange={setHousehold}
                valueLabel={`${household}`}
              />
            </div>

            {/* Results */}
            <div className="md:flex-1 flex flex-col gap-4">
              <div
                className="flex flex-col sm:flex-row gap-4"
                role="status"
                aria-live="polite"
              >
                {/* Money saved */}
                <div className="flex-1 p-6 rounded-[12px] bg-[#0d0c07] text-white flex flex-col justify-center">
                  <span className="text-[13px] font-medium text-[#cfcdc6] leading-[20px] mb-1">
                    {t("landingB.calculator.moneyResultLabel")}
                  </span>
                  <span className="text-[40px] font-extrabold leading-[44px] text-[#FFCB00] tabular-nums">
                    {currencySymbol}
                    {moneyPerMonth.toLocaleString()}
                  </span>
                  <span className="text-[12px] text-[#a3a199] leading-[18px] mt-2">
                    {t("landingB.calculator.moneyNote")}
                  </span>
                </div>

                {/* Time saved */}
                <div className="flex-1 p-6 rounded-[12px] border-[0.5px] border-[#ebebea] bg-white flex flex-col justify-center">
                  <span className="text-[13px] font-medium text-[#56554d] leading-[20px] mb-1">
                    {t("landingB.calculator.timeResultLabel")}
                  </span>
                  <span className="text-[40px] font-extrabold leading-[44px] text-[#0d0c07] tabular-nums">
                    {hoursPerMonth}
                    <span className="text-[20px] font-bold ml-1">
                      {t("landingB.calculator.hours")}
                    </span>
                  </span>
                  <span className="text-[12px] text-[#a09f9c] leading-[18px] mt-2">
                    {t("landingB.calculator.timeNote")}
                  </span>
                </div>
              </div>

              <p className="text-[11px] text-[#a09f9c] leading-[16px] text-center">
                {t("landingB.calculator.disclaimer")}
              </p>
            </div>
          </div>
        </div>
        {/* Decorative bread illustration (matches the live landing) */}
        <div
          className="absolute right-[-40px] md:right-[-24%] bottom-[0px] w-[160px] md:w-[220px] pointer-events-none"
          aria-hidden="true"
        >
          <img
            src="/assets/landing/features-bread.png"
            alt=""
            width="604"
            height="737"
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-contain"
          />
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator;
